import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Award,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  Shield,
  FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificationManagement = () => {
  const { isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [certifications, setCertifications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [certificationTypes, setCertificationTypes] = useState([]);
  
  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [certNumber, setCertNumber] = useState("");
  const [notes, setNotes] = useState("");
  
  // New type form
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeColor, setNewTypeColor] = useState("#3b82f6");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [certsRes, employeesRes, typesRes] = await Promise.all([
        axios.get(`${API}/certifications`),
        axios.get(`${API}/employees?current_only=true`),
        axios.get(`${API}/categories/`, { params: { category_type: 'certification' } }),
      ]);
      setCertifications(certsRes.data);
      setEmployees(employeesRes.data);
      setCertificationTypes(typesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCertification = async () => {
    if (!selectedEmployee || !selectedType) {
      toast.error("Please select an employee and certification type");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/certifications`, {
        employee_id: selectedEmployee,
        certification_type: selectedType,
        issue_date: issueDate || null,
        expiry_date: expiryDate || null,
        certificate_number: certNumber || null,
        notes: notes || null,
      });
      toast.success("Certification added");
      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to add certification");
    } finally {
      setSaving(false);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName) {
      toast.error("Please enter a type name");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/categories/`, {
        name: newTypeName,
        category_type: "certification",
        color: newTypeColor,
      });
      toast.success("Certification type added");
      setTypeDialogOpen(false);
      setNewTypeName("");
      setNewTypeColor("#3b82f6");
      fetchData();
    } catch (error) {
      toast.error("Failed to add certification type");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCertification = async (certId) => {
    if (!window.confirm("Are you sure you want to delete this certification?")) return;
    
    try {
      await axios.delete(`${API}/certifications/${certId}`);
      toast.success("Certification deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete certification");
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm("Delete this certification type?")) return;
    
    try {
      await axios.delete(`${API}/categories/${typeId}`);
      toast.success("Certification type deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete type");
    }
  };

  const resetForm = () => {
    setSelectedEmployee("");
    setSelectedType("");
    setIssueDate("");
    setExpiryDate("");
    setCertNumber("");
    setNotes("");
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.name : "Unknown";
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: "none", label: "No Expiry", color: "bg-gray-500/10 text-gray-600" };
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: "expired", label: "Expired", color: "bg-red-500/10 text-red-600 border-red-500/30" };
    } else if (daysUntilExpiry <= 30) {
      return { status: "warning", label: `${daysUntilExpiry}d left`, color: "bg-amber-500/10 text-amber-600 border-amber-500/30" };
    } else if (daysUntilExpiry <= 90) {
      return { status: "soon", label: `${daysUntilExpiry}d left`, color: "bg-blue-500/10 text-blue-600 border-blue-500/30" };
    }
    return { status: "valid", label: "Valid", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" };
  };

  // Get certifications expiring soon
  const expiringCerts = certifications.filter(c => {
    if (!c.expiry_date) return false;
    const daysUntil = Math.ceil((new Date(c.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 90 && daysUntil >= 0;
  });

  const expiredCerts = certifications.filter(c => {
    if (!c.expiry_date) return false;
    return new Date(c.expiry_date) < new Date();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="certification-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6" />
            Certification Management
          </h1>
          <p className="text-muted-foreground">
            Track employee certifications and expiry dates
          </p>
        </div>
        {isOfficeManager && (
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-certification-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Certification
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{certifications.length}</p>
                <p className="text-sm text-muted-foreground">Total Certifications</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-emerald-500/10">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {certifications.filter(c => !c.expiry_date || new Date(c.expiry_date) > new Date()).length}
                </p>
                <p className="text-sm text-muted-foreground">Valid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-500/10">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{expiringCerts.length}</p>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-red-500/10">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{expiredCerts.length}</p>
                <p className="text-sm text-muted-foreground">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Certifications</TabsTrigger>
          <TabsTrigger value="expiring">
            Expiring Soon
            {expiringCerts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{expiringCerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="types">Certification Types</TabsTrigger>
        </TabsList>

        {/* All Certifications */}
        <TabsContent value="all" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>All Certifications</CardTitle>
              <CardDescription>
                {certifications.length} certification{certifications.length !== 1 ? "s" : ""} on record
              </CardDescription>
            </CardHeader>
            <CardContent>
              {certifications.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No certifications recorded yet.</p>
                  <p className="text-sm">Click "Add Certification" to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Certification</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certifications.map((cert) => {
                      const expiry = getExpiryStatus(cert.expiry_date);
                      return (
                        <TableRow key={cert.id}>
                          <TableCell className="font-medium">
                            {getEmployeeName(cert.employee_id)}
                          </TableCell>
                          <TableCell>{cert.certification_type}</TableCell>
                          <TableCell>
                            {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            {cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={expiry.color}>
                              {expiry.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCertification(cert.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expiring Soon */}
        <TabsContent value="expiring" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Certifications Expiring Soon
              </CardTitle>
              <CardDescription>
                Certifications expiring within the next 90 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiringCerts.length === 0 && expiredCerts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-600 opacity-50" />
                  <p>All certifications are up to date!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {expiredCerts.length > 0 && (
                    <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                      <h4 className="font-semibold text-red-600 flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-4 w-4" />
                        Expired ({expiredCerts.length})
                      </h4>
                      <div className="space-y-2">
                        {expiredCerts.map(cert => (
                          <div key={cert.id} className="flex justify-between items-center text-sm">
                            <span>{getEmployeeName(cert.employee_id)} - {cert.certification_type}</span>
                            <span className="text-red-600">
                              Expired {new Date(cert.expiry_date).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {expiringCerts.length > 0 && (
                    <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <h4 className="font-semibold text-amber-600 flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4" />
                        Expiring Soon ({expiringCerts.length})
                      </h4>
                      <div className="space-y-2">
                        {expiringCerts.map(cert => {
                          const daysLeft = Math.ceil((new Date(cert.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                          return (
                            <div key={cert.id} className="flex justify-between items-center text-sm">
                              <span>{getEmployeeName(cert.employee_id)} - {cert.certification_type}</span>
                              <span className="text-amber-600">
                                {daysLeft} days left
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certification Types */}
        <TabsContent value="types" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Certification Types</CardTitle>
              <CardDescription>
                Manage the types of certifications tracked
              </CardDescription>
            </CardHeader>
            <CardContent>
              {certificationTypes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No certification types defined yet.</p>
                  <p className="text-sm">Click "Add Type" to create one.</p>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {certificationTypes.map((type) => (
                    <div
                      key={type.id}
                      className="p-4 border rounded-lg flex items-center justify-between"
                      style={{ borderLeftColor: type.color, borderLeftWidth: "4px" }}
                    >
                      <div className="flex items-center gap-3">
                        <Award className="h-5 w-5" style={{ color: type.color }} />
                        <span className="font-medium">{type.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteType(type.id)}
                        className="text-destructive hover:text-destructive h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Certification Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Certification</DialogTitle>
            <DialogDescription>
              Add a certification record for an employee
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="cert-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Certification Type *</Label>
              <CategoryManager
                categoryType="certification"
                label="Certification Type"
                onCategoriesChange={fetchData}
                showAsDropdown={true}
                selectedValue={selectedType}
                onSelectionChange={setSelectedType}
                allowAll={false}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Certificate Number</Label>
              <Input
                value={certNumber}
                onChange={(e) => setCertNumber(e.target.value)}
                placeholder="e.g., CERT-2026-001"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCertification} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Certification Type</DialogTitle>
            <DialogDescription>
              Create a new certification category
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type Name *</Label>
              <Input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="e.g., First Aid, Safety Training"
                data-testid="new-type-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={newTypeColor}
                  onChange={(e) => setNewTypeColor(e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <span className="text-sm text-muted-foreground">{newTypeColor}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddType} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificationManagement;
